import { useEffect, useState } from 'react';
import { AlertTriangle, Link2Off, MessageSquare, Paperclip, Play, Square } from 'lucide-react';
import { PRIORITY_COLOURS, type Priority } from '@virtuetasks/types';
import { cn, formatMinutes } from '@/lib/utils';
import { UserAvatar } from '@/components/ui/avatar';
import type { TaskView } from '@/features/tasks/api';

/** Elapsed time re-reads once a minute — the control shows whole minutes. */
const ELAPSED_TICK_MS = 30_000;

export function PriorityPill({ priority, onClick }: { priority: Priority; onClick?: () => void }) {
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      title={`Priority ${priority}`}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium leading-none',
        onClick && 'transition-colors duration-[120ms] hover:bg-[var(--surface-2)]',
      )}
      style={{ color: PRIORITY_COLOURS[priority] }}
    >
      <span
        className="size-1.5 rounded-full"
        style={{ background: PRIORITY_COLOURS[priority] }}
        aria-hidden
      />
      {priority}
    </Tag>
  );
}

/** Overdue reads red; everything else stays quiet. */
export function DueChip({ dueDate, done }: { dueDate?: string; done?: boolean }) {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const overdue = !done && due < startOfToday;
  const today = due >= startOfToday && due < new Date(startOfToday.getTime() + 86_400_000);

  return (
    <span
      className="vt-mono whitespace-nowrap text-[11px]"
      style={{ color: overdue ? 'var(--danger)' : today ? 'var(--warn)' : 'var(--text-muted)' }}
      title={due.toLocaleString()}
    >
      {overdue ? 'Overdue ' : ''}
      {due.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
    </span>
  );
}

export interface TaskRowPeople {
  [userId: string]: { name: string; avatarUrl?: string };
}

export interface TaskRowTimer {
  /** ISO start time when a timer is running on this task; absent means idle. */
  runningSince?: string;
  onStart: () => void;
  onStop?: () => void;
}

/** `formatMinutes` reads 0 as "no value"; a timer that just started is `0m`. */
function elapsedLabel(since: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 60_000));
  return minutes === 0 ? '0m' : formatMinutes(minutes);
}

/**
 * The timer control — a real flex item, never an overlay.
 *
 * V6-06: this used to be absolutely positioned over the row on My Day, which
 * put it straight on top of the assignee avatars pinned to the right edge.
 * Living in its own slot means it cannot collide with anything, including
 * when it widens to show elapsed time.
 */
function TimerControl({ taskKey, timer }: { taskKey: string; timer: TaskRowTimer }) {
  const running = Boolean(timer.runningSince);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((n) => n + 1), ELAPSED_TICK_MS);
    return () => clearInterval(id);
  }, [running]);

  const label = running ? `Stop timer on ${taskKey}` : `Start timer on ${taskKey}`;

  return (
    <span className="flex min-w-[26px] shrink-0 justify-end" data-testid="task-row-timer-slot">
      <button
        type="button"
        data-testid="task-row-timer"
        data-task-key={taskKey}
        data-running={running ? 'true' : 'false'}
        aria-label={label}
        title={label}
        // Keeps a click on the control out of the drag sensors on My Day.
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (running) timer.onStop?.();
          else timer.onStart();
        }}
        className={cn(
          'inline-flex items-center gap-1 whitespace-nowrap rounded-[6px] px-1 py-1 text-[11px] leading-none transition-colors duration-[120ms] hover:bg-[var(--surface-2)] hover:text-[var(--accent)]',
          running ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]',
        )}
      >
        {running ? <Square className="size-3 fill-current" /> : <Play className="size-3.5" />}
        {timer.runningSince && <span className="vt-mono">{elapsedLabel(timer.runningSince)}</span>}
      </button>
    </span>
  );
}

export function TaskRow({
  task,
  statusName,
  people,
  selected,
  focused,
  timer,
  onSelect,
  onOpen,
}: {
  task: TaskView;
  statusName?: string;
  people: TaskRowPeople;
  selected?: boolean;
  focused?: boolean;
  /** Renders the timer control in its own slot. Omit and no slot is drawn. */
  timer?: TaskRowTimer;
  onSelect?: (checked: boolean) => void;
  onOpen?: () => void;
}) {
  const done = task.statusCategory === 'done';
  const blocked = task.blockedBy.length > 0;
  const checklistDone = task.checklist.filter((c) => c.done).length;
  const totalSteps = task.subtaskCount + task.checklist.length;
  const doneSteps = task.subtaskDoneCount + checklistDone;

  return (
    <div
      role="row"
      data-testid="task-row"
      data-task-key={task.key}
      tabIndex={focused ? 0 : -1}
      onDoubleClick={onOpen}
      className={cn(
        'group relative flex items-center gap-2 border-b border-[var(--border)] pl-3 pr-2 text-[13px] transition-colors duration-[120ms] last:border-b-0',
        selected ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--surface-2)]',
      )}
      style={{ minHeight: 'var(--row-height)' }}
    >
      <span className="vt-spine" style={{ background: PRIORITY_COLOURS[task.priority] }} aria-hidden />

      {onSelect && (
        <input
          type="checkbox"
          checked={Boolean(selected)}
          onChange={(e) => onSelect(e.target.checked)}
          aria-label={`Select ${task.key}`}
          className="ml-1 shrink-0"
        />
      )}

      <button
        type="button"
        onClick={onOpen}
        className="vt-mono w-[68px] shrink-0 text-left text-[11px] vt-muted hover:text-[var(--accent)]"
      >
        {task.key}
      </button>

      <button
        type="button"
        onClick={onOpen}
        className={cn('min-w-0 flex-1 truncate text-left', done && 'line-through vt-muted')}
        title={task.title}
      >
        {task.title}
      </button>

      {blocked && (
        <span title={task.blockedReason ?? 'Blocked by another task'} className="shrink-0 text-[var(--danger)]">
          <Link2Off className="size-3.5" />
        </span>
      )}

      {totalSteps > 0 && (
        <span className="vt-mono hidden shrink-0 text-[11px] vt-muted sm:inline" title="Subtasks and checklist">
          {doneSteps}/{totalSteps}
        </span>
      )}

      {task.commentCount > 0 && (
        <span className="hidden shrink-0 items-center gap-0.5 text-[11px] vt-muted sm:flex">
          <MessageSquare className="size-3" />
          {task.commentCount}
        </span>
      )}
      {task.attachmentCount > 0 && (
        <span className="hidden shrink-0 items-center gap-0.5 text-[11px] vt-muted sm:flex">
          <Paperclip className="size-3" />
          {task.attachmentCount}
        </span>
      )}

      {task.estimateMinutes ? (
        <span className="vt-mono hidden w-[52px] shrink-0 text-right text-[11px] vt-muted md:inline">
          {formatMinutes(task.estimateMinutes)}
        </span>
      ) : (
        <span className="hidden w-[52px] shrink-0 md:inline" />
      )}

      {statusName && (
        <span className="hidden w-[92px] shrink-0 truncate text-[11px] vt-muted lg:inline" title={statusName}>
          {statusName}
        </span>
      )}

      <PriorityPill priority={task.priority} />

      <span className="w-[74px] shrink-0 text-right">
        <DueChip dueDate={task.dueDate} done={done} />
      </span>

      <span className="flex w-[52px] shrink-0 justify-end -space-x-1.5" data-testid="task-row-avatars">
        {task.assigneeIds.slice(0, 2).map((id) => (
          <UserAvatar
            key={id}
            id={id}
            name={people[id]?.name ?? 'Unknown'}
            src={people[id]?.avatarUrl}
            size={20}
            className="ring-1 ring-[var(--surface)]"
          />
        ))}
        {task.assigneeIds.length === 0 && (
          <span className="grid size-5 place-items-center rounded-full border border-dashed border-[var(--border)]">
            <AlertTriangle className="size-2.5 text-[var(--text-muted)]" />
          </span>
        )}
      </span>

      {timer && <TimerControl taskKey={task.key} timer={timer} />}
    </div>
  );
}
